import ArrowBack from '@material-ui/icons/ArrowBack';
import arrayMutators from 'final-form-arrays';
import startCase from 'lodash.startcase';
import {
  FieldResponse,
  FieldValueResponse,
  FieldValueWithVersionsResponse,
  NullableString,
  SaveFieldValueRequest,
} from 'mapistry-shared';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Form } from 'react-final-form';
import styled from 'styled-components';
import { Loading } from '../../../components/Loading';
import { useConfirmBypassValidationAndSaveEntry } from '../../../hooks/logs/useConfirmBypassValidationAndSaveEntry';
import { Button } from '../../Button';
import { IconButton } from '../../Button/IconButton';
import { GenericError } from '../../errorDisplays/GenericError';
import { BaseModal } from '../../modals/BaseModal';
import { VisuallyHidden } from '../../VisuallyHidden';
import { EditEntryForm } from './EditEntryForm';
import { EditHistoricalValueList } from './EditHistoricalValueList';

const SaveButton = styled(Button)`
  width: 5rem;
`;

const TitleWithBackButton = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const SecondPageContainerDiv = styled.div<{ $minHeight: number }>`
  min-height: ${({ $minHeight }) => $minHeight}px;
`;

type BaseEditEntryModalProps<EntryResponseType> = {
  children: React.ReactNode;
  entryId?: string;
  entryType: 'logEntry' | 'resource';
  fields?: FieldResponse[];
  fieldValues?: Record<
    FieldResponse['id'],
    FieldValueResponse | FieldValueWithVersionsResponse
  >;
  getEntryName: (response: EntryResponseType) => string;
  getHistoricalValueModalTitle?: (field: FieldResponse) => string;
  instructions?: NullableString;
  modalId?: string;
  isLoading: boolean;
  onClose: () => void;
  open: boolean;
  organizationId: string;
  projectId: string | undefined;
  saver: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formValues: Record<string, any>,
    fieldValues: SaveFieldValueRequest[],
  ) => Promise<EntryResponseType | undefined>;
};

export function BaseEditEntryModal<EntryResponseType>({
  children,
  entryId,
  entryType,
  fields,
  fieldValues,
  getEntryName,
  getHistoricalValueModalTitle = () => '',
  instructions,
  modalId,
  isLoading,
  onClose,
  open,
  organizationId,
  projectId,
  saver,
}: BaseEditEntryModalProps<EntryResponseType>) {
  const [selectedVersionedField, setSelectedVersionedField] =
    useState<FieldResponse | null>(null);

  const modalContentRef = useRef<HTMLDivElement>(null);
  // remembers where the user was scrolled to on the main page, so we can
  //  restore it when navigating back from the historical values list
  const mainPageScrollTopRef = useRef(0);
  // remembers how tall the main page is, so the historical values list page
  //  doesn't shrink the modal below that height when it has less content than the main page
  const [mainPageContentHeight, setMainPageContentHeight] = useState(0);
  const entryTypeDisplayName =
    entryType === 'logEntry' ? 'Log entry' : 'Resource';

  const selectVersionedField = useCallback((field: FieldResponse) => {
    mainPageScrollTopRef.current = modalContentRef.current?.scrollTop ?? 0;
    setMainPageContentHeight(modalContentRef.current?.clientHeight ?? 0);
    setSelectedVersionedField(field);
  }, []);

  useEffect(() => {
    modalContentRef.current?.scrollTo({
      top: selectedVersionedField ? 0 : mainPageScrollTopRef.current,
    });
  }, [selectedVersionedField]);

  const title = useMemo(() => {
    if (!selectedVersionedField) {
      return entryId
        ? `Edit ${startCase(entryTypeDisplayName)}`
        : `Add New ${startCase(entryTypeDisplayName)}`;
    }
    return (
      <TitleWithBackButton>
        <IconButton onClick={() => setSelectedVersionedField(null)}>
          <ArrowBack />
          <VisuallyHidden>Back</VisuallyHidden>
        </IconButton>
        {getHistoricalValueModalTitle(selectedVersionedField)}
      </TitleWithBackButton>
    );
  }, [
    entryId,
    entryTypeDisplayName,
    getHistoricalValueModalTitle,
    selectedVersionedField,
  ]);

  const { handleSave, persistValidatedForm } =
    useConfirmBypassValidationAndSaveEntry<EntryResponseType>({
      entryId,
      entryType,
      fields,
      getEntryName,
      onAfterSave: onClose,
      saver,
    });

  // children prop should only be truthy when it is able to properly render
  const canRenderForm = !!children && !!fields;

  const modalContents = useMemo(() => {
    if (!open) return null;
    if (isLoading) return <Loading />;
    if (!canRenderForm) return <GenericError />;
    if (selectedVersionedField) {
      return (
        <SecondPageContainerDiv $minHeight={mainPageContentHeight}>
          <EditHistoricalValueList
            field={selectedVersionedField}
            organizationId={organizationId}
            projectId={projectId}
          />
        </SecondPageContainerDiv>
      );
    }
    return (
      <EditEntryForm
        entryTypeDisplayName={entryTypeDisplayName}
        fieldValues={fieldValues}
        fields={fields}
        instructions={instructions}
        onVersionedFieldSelected={selectVersionedField}
        organizationId={organizationId}
        projectId={projectId}
      >
        {children}
      </EditEntryForm>
    );
  }, [
    canRenderForm,
    children,
    entryTypeDisplayName,
    fieldValues,
    fields,
    instructions,
    isLoading,
    mainPageContentHeight,
    open,
    organizationId,
    projectId,
    selectVersionedField,
    selectedVersionedField,
  ]);

  return (
    <Form
      mutators={{ ...arrayMutators }}
      onSubmit={persistValidatedForm}
      subscription={{
        errors: true,
        invalid: true,
        pristine: true,
        submitting: true,
        values: true,
      }}
      // initial values are set on the specific fields; it's more straightforward since the shape of this
      //  form is determined by fetched data (the log or resource type config)
    >
      {({ errors, handleSubmit, invalid, pristine, submitting, values }) => (
        <BaseModal
          buttons={[
            <Button
              color="primary"
              disabled={submitting}
              key="cancel"
              onClick={onClose}
              variant="text"
            >
              Cancel
            </Button>,
            <SaveButton
              color="primary"
              disabled={submitting || pristine || !canRenderForm || isLoading}
              key="save"
              onClick={() =>
                handleSave({ invalid, errors, values, handleSubmit })
              }
              variant="contained"
            >
              {submitting ? 'Saving...' : 'Save'}
            </SaveButton>,
          ]}
          contentRef={modalContentRef}
          id={modalId}
          onClose={submitting ? undefined : onClose}
          open={open}
          showCloseButton
          title={title}
        >
          {modalContents}
        </BaseModal>
      )}
    </Form>
  );
}
